import React, { useState, useCallback, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import Select from "react-select";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles.js";
import CustomDateTimePicker from "../../components/DateTimePicker/dateTimePicker.js";
import { useSelector } from "react-redux";
import moment from "moment";
import CountryCitySelector from "../../components/CustomComponents/DropdownSelections/CountryCitySelector.jsx";
import useThemeColor from "../../hooks/useThemeColor.js";
import useDarkMode from "../../hooks/useDarkMode.js";
import CompanyStaff from "./companyStaff.js";
import CompanyDevices from "./companyDevices.js";
import CompanySubscriptions from "./companySubscriptions.js";
import CompanyInvoices from "./companyInvoices.js";
import CompanyEmailSender from "./companyEmailSender.js";
import "./UpdateCompany.css";

const COMPANY_TABS = [
  { id: "general", label: "General", icon: "fa-building" },
  { id: "staff", label: "Staff", icon: "fa-users" },
  { id: "devices", label: "Devices", icon: "fa-mobile-alt" },
  { id: "subscriptions", label: "Subscriptions", icon: "fa-credit-card" },
  { id: "invoices", label: "Invoices", icon: "fa-file-invoice" },
  { id: "emailSender", label: "Email Sender", icon: "fa-envelope" },
];

const UpdateCompany = () => {
  const { CompID } = useParams();

  const navigate = useNavigate();
  const authCompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [companyData, setCompanyData] = useState({
    // From Company.cs
    CompID: parseInt(CompID),
    LocalizeID: "en",
    CompName: "",
    OwnerName: "",
    LegalCompanyName: "",
    CompDetail: "",
    CompEmail: "",
    CompPhone: "",
    CompFax: "",
    CompIndustryType: "Hospitality",
    CompCurrency: "",
    CompCountry: "",
    CompCity: "",
    CompAddress: "",
    CompSecCode: "",
    CompType: "",
    CompRemarks: "",
    CompRegDate: moment().format("YYYY-MM-DD"), // Set to current date by default
    CompExpireDate: null,
    CompIcon: "/img/serveiq2.png",
    CompCoverImage: "",
    CompHeaderLogo: "",
    CompPNotificationLimit_PerDay: 10,
    CompTimeZone: "W. Europe Standard Time",
    CompUTCTimeZone: "(UTC+01:00) Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna",
    CompLatitude: "0",
    CompLongitude: "0",
    IsNeedtoSyncDB: false,
    AndroidAppver: "",
    IOSAppver: "",
    DailyForecastBracket: false,
    IsArchieve: false,
    CompNumberOfDaysToShowExpiryWarning: 0,
    CompRecoveryEmail: "",
    CompRecoveryPhoneNumber: "",
    CompLanguage: "en",
    CompWebsite: "",
    CompFacebooklink: "",
    CompInstagramLink: "",
    CompYoutubeLink: "",
    CompTikTokLink: "",
    CompGoogleReview: "",
    CompPublish: false,
    CompanySlug: "",

    // From Company2.cs
    ParentComID: 0,
    LocationLicences: 0,
    IsCallerID: false,
    ISMobileApp: false,
    IsKitchenApp: false,
    ISWebApp: false,
    IsTVApp: false,
    IsWaiterApp: false,
    IsOnlineOrderingAllowed: true,
    IsReservationAllowed: true,
    CompMaxIPadLicense: 1,
    WaiterAppMaxDevices: 0,
    CompMaxUserLicense: 1,
    CompMonthlySmsLimit: 0,
    CompMonthlyWhatsAppLimit: 0,
    // Set current date for hidden date fields as default to avoid DateTime.MinValue (1/1/0001) SQL errors
    MobileAppExpireDate: moment().format("YYYY-MM-DD"),
    WebAppExpireDate: moment().format("YYYY-MM-DD"),
    WaiterAppExpireDate: moment().format("YYYY-MM-DD"),
  });
  const [industryTypes] = useState([
    { value: "Hospitality", label: "Hospitality" },
    { value: "Retail", label: "Retail" },
    { value: "Saloon", label: "Saloon" },
  ]);
  const [currencies, setCurrencies] = useState([]);
  const [timeZones, setTimeZones] = useState([]);
  const [selectedIndustryType, setSelectedIndustryType] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [selectedTimeZone, setSelectedTimeZone] = useState(null);

  // Fetch currencies
  const fetchCurrencies = useCallback(async () => {
    try {
      setLoading(true);
      console.log("Fetching currencies from:", APIs.GET_ALL_CURRENCIES);
      const response = await axiosInstance.get(APIs.GET_ALL_CURRENCIES);
      console.log("Currency API response:", response.data);

      if (response.data && response.data.data) {
        const currencyOptions = response.data.data.map((currency) => ({
          value: currency.value,
          label: currency.text,
        }));
        console.log("Currency options:", currencyOptions);
        setCurrencies(currencyOptions);
      } else {
        console.warn("No currency data found in response");
        // Fallback currencies if API fails
        const fallbackCurrencies = [
          { value: "USD", label: "US Dollar" },
          { value: "EUR", label: "Euro" },
          { value: "GBP", label: "British Pound" },
          { value: "AED", label: "UAE Dirham" },
        ];
        setCurrencies(fallbackCurrencies);
      }
    } catch (err) {
      console.error("Currency fetch error:", err);
      showErrorToast("Failed to load currencies");
      // Fallback currencies if API fails
      const fallbackCurrencies = [
        { value: "USD", label: "US Dollar" },
        { value: "EUR", label: "Euro" },
        { value: "GBP", label: "British Pound" },
        { value: "AED", label: "UAE Dirham" },
      ];
      setCurrencies(fallbackCurrencies);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTimeZones = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_ALL_TIMEZONE);
      const list = response.data?.data || [];
      setTimeZones(
        list.map((tz) => ({
          value: tz.value,
          label: tz.text,
        }))
      );
    } catch (err) {
      console.error("Time zone fetch error:", err);
      showErrorToast("Failed to load time zones");
      setTimeZones([]);
    }
  }, []);

  // Fetch company details
  const fetchCompanyDetails = useCallback(async () => {

    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.Get_COMPANY_BY_ID, {
        params: { CompID: CompID },
      });

      console.log(response.data);

      const data = response.data.data || response.data;

      const formattedData = {
        // From Company.cs
        CompID: parseInt(CompID),
        LocalizeID: data.localizeID ?? data.LocalizeID ?? "en",
        CompName: data.compName || "",
        OwnerName: data.ownerName || data.OwnerName || "",
        LegalCompanyName: data.legalCompanyName || data.LegalCompanyName || "",
        CompDetail: data.compDetail || "",
        CompEmail: data.compEmail || "",
        CompPhone: data.compPhone || "",
        CompFax: data.compFax || "",
        CompIndustryType: data.compIndustryType || "Hospitality",
        CompCurrency: data.compCurrency || "",
        CompCountry: data.compCountry || "",
        CompCity: data.compCity || "",
        CompAddress: data.compAddress || "",
        CompSecCode: data.compSecCode || "",
        CompType: data.compType || "",
        CompRemarks: data.compRemarks || "",
        CompRegDate: data.compRegDate ? moment(data.compRegDate).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
        CompExpireDate: data.compExpireDate ? moment(data.compExpireDate).format("YYYY-MM-DD") : null,
        CompIcon: data.compIcon || "/img/serveiq2.png",
        CompCoverImage: data.compCoverImage ?? data.CompCoverImage ?? "",
        CompHeaderLogo: data.compHeaderLogo || "",
        CompPNotificationLimit_PerDay: data.compPNotificationLimit_PerDay || 10,
        CompTimeZone: data.compTimeZone || "W. Europe Standard Time",
        CompUTCTimeZone: data.compUTCTimeZone || "(UTC+01:00) Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna",
        CompLatitude: data.compLatitude || "0",
        CompLongitude: data.compLongitude || "0",
        IsNeedtoSyncDB: data.isNeedtoSyncDB || false,
        AndroidAppver: data.androidAppver || "",
        IOSAppver: data.iosAppver || "",
        DailyForecastBracket: data.dailyForecastBracket || false,
        IsArchieve: data.isArchieve || false,
        CompNumberOfDaysToShowExpiryWarning: data.compNumberOfDaysToShowExpiryWarning || 0,
        CompRecoveryEmail: data.compRecoveryEmail || "",
        CompRecoveryPhoneNumber: data.compRecoveryPhoneNumber || "",
        CompLanguage: data.compLanguage || "en",
        CompWebsite: data.compWebsite || "",
        CompFacebooklink: data.compFacebooklink || "",
        CompInstagramLink: data.compInstagramLink || "",
        CompYoutubeLink: data.compYoutubeLink || "",
        CompTikTokLink: data.compTikTokLink || "",
        CompGoogleReview: data.compGoogleReview || "",
        CompPublish: data.compPublish || false,
        CompanySlug: data.companySlug ?? data.CompanySlug ?? "",

        // From Company2.cs
        ParentComID: data.parentComID || 0,
        LocationLicences: data.locationLicences || 0,
        IsCallerID: data.isCallerID || false,
        ISMobileApp: data.isMobileApp || false,
        IsKitchenApp: data.isKitchenApp || false,
        ISWebApp: data.isWebApp || false,
        IsTVApp: data.isTVApp || false,
        IsWaiterApp: data.isWaiterApp || false,
        IsOnlineOrderingAllowed: data.isOnlineOrderingAllowed ?? true,
        IsReservationAllowed: data.isReservationAllowed ?? true,
        CompMaxIPadLicense: data.compMaxIPadLicense || 1,
        WaiterAppMaxDevices: data.waiterAppMaxDevices || 0,
        CompMaxUserLicense: data.compMaxUserLicense || 1,
        CompMonthlySmsLimit: data.compMonthlySmsLimit ?? 0,
        CompMonthlyWhatsAppLimit: data.compMonthlyWhatsAppLimit ?? 0,
        MobileAppExpireDate: data.mobileAppExpireDate ? moment(data.mobileAppExpireDate).format("YYYY-MM-DD") : null,
        WebAppExpireDate: data.webAppExpireDate ? moment(data.webAppExpireDate).format("YYYY-MM-DD") : null,
        WaiterAppExpireDate: data.waiterAppExpireDate ? moment(data.waiterAppExpireDate).format("YYYY-MM-DD") : null,
      };

      setCompanyData(formattedData);
      setSelectedIndustryType(industryTypes.find((type) => type.value === data.compIndustryType));
      setSelectedCountry(data.compCountry || "");
      setSelectedCity(data.compCity || "");
    } catch (error) {
      showErrorToast("Failed to load company details");
      console.error("Error fetching company:", error);
    } finally {
      setLoading(false);
    }
  }, [CompID, authCompID, industryTypes]);

  // Currencies load async; selectedCurrency must sync after options exist (fixes empty dropdown on load)
  useEffect(() => {
    const code = (companyData.CompCurrency || "").trim();
    if (!code || currencies.length === 0) {
      if (!code) setSelectedCurrency(null);
      return;
    }
    const match = currencies.find(
      (c) =>
        c.value === code ||
        String(c.value).toUpperCase() === code.toUpperCase()
    );
    setSelectedCurrency(match || null);
  }, [currencies, companyData.CompCurrency]);

  // Time zones load async; keep selected option in sync with company CompTimeZone
  useEffect(() => {
    const zoneId = (companyData.CompTimeZone || "").trim();
    if (!zoneId || timeZones.length === 0) {
      if (!zoneId) setSelectedTimeZone(null);
      return;
    }
    const match = timeZones.find((tz) => tz.value === zoneId);
    if (match) {
      setSelectedTimeZone(match);
      return;
    }
    // Fallback for stored Id not in list (show saved values)
    setSelectedTimeZone({
      value: zoneId,
      label: companyData.CompUTCTimeZone || zoneId,
    });
  }, [timeZones, companyData.CompTimeZone, companyData.CompUTCTimeZone]);

  const handleInputChange = useCallback((e, field) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setCompanyData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleDateChange = useCallback((newValue, field) => {
    setCompanyData((prev) => ({
      ...prev,
      [field]: newValue ? moment(newValue).format("YYYY-MM-DD") : null,
    }));
  }, []);

  const handleCountryCityChange = useCallback((data) => {
    setCompanyData((prev) => ({
      ...prev,
      ...data,
    }));
    if (data.CompCountry !== undefined) {
      setSelectedCountry(data.CompCountry);
    }
    if (data.CompCity !== undefined) {
      setSelectedCity(data.CompCity);
    }
  }, []);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async () => {
    const { CompName, OwnerName, LegalCompanyName, CompEmail, CompIndustryType, CompCurrency } = companyData;

    if (!CompName.trim()) {
      showErrorToast("Please provide a valid company name");
      return;
    }

    if (!OwnerName?.trim()) {
      showErrorToast("Please provide the restaurant owner name");
      return;
    }

    if (!LegalCompanyName?.trim()) {
      showErrorToast("Please provide the legal company name");
      return;
    }

    if (!CompEmail || !validateEmail(CompEmail)) {
      showErrorToast("Please provide a valid email");
      return;
    }

    if (!CompIndustryType) {
      showErrorToast("Please select an industry type");
      return;
    }

    if (!CompCurrency) {
      showErrorToast("Please select a currency");
      return;
    }

    if (!companyData.CompTimeZone?.trim()) {
      showErrorToast("Please select a time zone");
      return;
    }

    // Build submitData - only include date fields if they have valid values
    const submitData = {
      ...companyData,
      OwnerName: OwnerName.trim(),
      LegalCompanyName: LegalCompanyName.trim(),
      LocalizeID: companyData.LocalizeID || "en",
      CompID: parseInt(CompID),
      CompNumberOfDaysToShowExpiryWarning: parseInt(companyData.CompNumberOfDaysToShowExpiryWarning) || 0,
      CompPNotificationLimit_PerDay: parseInt(companyData.CompPNotificationLimit_PerDay) || 10,
      CompMaxIPadLicense: parseInt(companyData.CompMaxIPadLicense) || 1,
      CompMaxUserLicense: parseInt(companyData.CompMaxUserLicense) || 1,
      CompMonthlySmsLimit: parseInt(companyData.CompMonthlySmsLimit) || 0,
      CompMonthlyWhatsAppLimit: parseInt(companyData.CompMonthlyWhatsAppLimit) || 0,
      LocationLicences: parseInt(companyData.LocationLicences) || 0,
      WaiterAppMaxDevices: parseInt(companyData.WaiterAppMaxDevices) || 0,
      ParentComID: parseInt(companyData.ParentComID) || 0,
    };

    // Helper function to validate date
    const isValidDate = (dateString) => {
      if (!dateString) return false;
      const date = moment(dateString, "YYYY-MM-DD", true);
      return date.isValid() && date.year() >= 1753 && date.year() <= 9999;
    };

    // CompRegDate: Always include with current date as fallback (with time component for SQL)
    const regDate = companyData.CompRegDate || moment().format("YYYY-MM-DD");
    if (isValidDate(regDate)) {
      submitData.CompRegDate = moment(regDate).format("YYYY-MM-DDTHH:mm:ss");
    } else {
      submitData.CompRegDate = moment().format("YYYY-MM-DDTHH:mm:ss");
      console.warn("Invalid CompRegDate, using current date:", submitData.CompRegDate);
    }
    
    // CompExpireDate: Optional, only include if provided
    if (companyData.CompExpireDate && isValidDate(companyData.CompExpireDate)) {
      submitData.CompExpireDate = moment(companyData.CompExpireDate).format("YYYY-MM-DDTHH:mm:ss");
    }
    
    // Hidden date fields: Keep existing data intact (whatever came from API)
    // Only format if valid, otherwise use current date to avoid DateTime.MinValue errors
    if (companyData.MobileAppExpireDate) {
      if (isValidDate(companyData.MobileAppExpireDate)) {
        submitData.MobileAppExpireDate = moment(companyData.MobileAppExpireDate).format("YYYY-MM-DDTHH:mm:ss");
      } else {
        // If invalid date from API, use current date as fallback
        submitData.MobileAppExpireDate = moment().format("YYYY-MM-DDTHH:mm:ss");
      }
    }
    
    if (companyData.WebAppExpireDate) {
      if (isValidDate(companyData.WebAppExpireDate)) {
        submitData.WebAppExpireDate = moment(companyData.WebAppExpireDate).format("YYYY-MM-DDTHH:mm:ss");
      } else {
        // If invalid date from API, use current date as fallback
        submitData.WebAppExpireDate = moment().format("YYYY-MM-DDTHH:mm:ss");
      }
    }
    
    if (companyData.WaiterAppExpireDate) {
      if (isValidDate(companyData.WaiterAppExpireDate)) {
        submitData.WaiterAppExpireDate = moment(companyData.WaiterAppExpireDate).format("YYYY-MM-DDTHH:mm:ss");
      } else {
        // If invalid date from API, use current date as fallback
        submitData.WaiterAppExpireDate = moment().format("YYYY-MM-DDTHH:mm:ss");
      }
    }

    // Remove any null, undefined, or empty string values that might cause issues
    // Keep timezone fields even if UTC label is empty so CompTimeZone is always persisted.
    Object.keys(submitData).forEach((key) => {
      if (key === "CompTimeZone" || key === "CompUTCTimeZone") return;
      if (submitData[key] === null || submitData[key] === undefined || submitData[key] === "") {
        console.log(`Removing empty field: ${key} = ${submitData[key]}`);
        delete submitData[key];
      }
    });

    console.log("Final submitData (after cleanup):", JSON.stringify(submitData, null, 2));

    try {
      setLoading(true);
      const response = await axiosInstance.put(APIs.POST_UPDATE_COMPANY, submitData);

      if (response.data.status) {
        showSuccessToast(response.data.message || "Company updated successfully");
        navigate("/company/manageCompany");
      } else {
        showErrorToast(response.data.message || "Failed to update company. Please check all fields.");
      }
    } catch (error) {
      console.error("Error updating company:", error);
      
      // Handle different error types
      if (error.response?.status === 404) {
        showErrorToast("Update company endpoint not found. Please check server configuration.");
      } else if (error.response?.status === 400) {
        // Validation error
        const errorData = error.response.data;
        if (errorData?.errors) {
          // Extract first error message
          const firstError = Object.values(errorData.errors)[0];
          const errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
          showErrorToast(errorMessage || "Validation error. Please check all fields.");
        } else if (errorData?.title) {
          showErrorToast(errorData.title);
        } else if (errorData?.message) {
          showErrorToast(errorData.message);
        } else {
          showErrorToast("Invalid data. Please check all required fields.");
        }
      } else if (error.response?.data?.message) {
        showErrorToast(error.response.data.message);
      } else if (error.response?.data?.title) {
        showErrorToast(error.response.data.title);
      } else if (error.message) {
        showErrorToast(`Error: ${error.message}`);
      } else {
        showErrorToast("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrencies();
    fetchTimeZones();
    fetchCompanyDetails();
  }, [fetchCurrencies, fetchTimeZones, fetchCompanyDetails]);

  const renderTabContent = () => {
    switch (activeTab) {
      case "general":
        return renderGeneralTab();
      case "staff":
        return (
          <CompanyStaff
            CompID={CompID}
            companyCountry={companyData.CompCountry}
            companyCity={companyData.CompCity}
            showAddForm={showAddStaffModal}
            setShowAddForm={setShowAddStaffModal}
          />
        );
      case "devices":
        return <CompanyDevices CompID={CompID} />;
      case "subscriptions":
        return <CompanySubscriptions CompID={CompID} />;
      case "invoices":
        return <CompanyInvoices CompID={CompID} />;
      case "emailSender":
        return <CompanyEmailSender CompID={CompID} />;
      default:
        return renderGeneralTab();
    }
  };

  const renderGeneralTab = () => {
    const textareaStyle = {
      borderRadius: "10px",
      border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
      background: isDarkMode ? "#2d3436" : "#F9F9F9",
      color: isDarkMode ? "#ffffff" : "#495057",
    };

    return (
      <>
        <section className="uc-section uc-section--nested">
          <div className="uc-section__header">
            <h5>General</h5>
            <p>Company profile, contact details, and location</p>
          </div>
          <div className="uc-section__body">
            <div className="row mb-3">
              <div className="col-md-6">
                <FormInput
                  label="Company Name *"
                  placeHolder="Enter Company Name"
                  type="text"
                  value={companyData.CompName}
                  onchange={(e) => handleInputChange(e, "CompName")}
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Owner Name *"
                  placeHolder="Enter Restaurant Owner Name"
                  type="text"
                  value={companyData.OwnerName}
                  onchange={(e) => handleInputChange(e, "OwnerName")}
                />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <FormInput
                  label="Legal Company Name *"
                  placeHolder="e.g. Indian Kitchen LTD"
                  type="text"
                  value={companyData.LegalCompanyName}
                  onchange={(e) => handleInputChange(e, "LegalCompanyName")}
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Email"
                  placeHolder="Enter Email Address"
                  type="email"
                  value={companyData.CompEmail}
                  onchange={(e) => handleInputChange(e, "CompEmail")}
                />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <FormInput
                  label="Company slug"
                  placeHolder="Online ordering URL slug"
                  type="text"
                  value={companyData.CompanySlug}
                  onchange={(e) => handleInputChange(e, "CompanySlug")}
                />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <FormInput
                  label="Phone Number"
                  placeHolder="Enter Phone Number"
                  type="tel"
                  value={companyData.CompPhone}
                  onchange={(e) => handleInputChange(e, "CompPhone")}
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Fax Number"
                  placeHolder="Enter Fax Number"
                  type="tel"
                  value={companyData.CompFax}
                  onchange={(e) => handleInputChange(e, "CompFax")}
                />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <label className="uc-label">Industry Type</label>
                <Select
                  value={selectedIndustryType}
                  onChange={(option) => {
                    setSelectedIndustryType(option);
                    setCompanyData({
                      ...companyData,
                      CompIndustryType: option ? option.value : "",
                    });
                  }}
                  options={industryTypes}
                  styles={dropdownStyles}
                  placeholder="Select Industry Type"
                  isClearable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuShouldBlockScroll={false}
                  menuShouldScrollIntoView={false}
                  closeMenuOnScroll={false}
                />
              </div>
              <div className="col-md-6">
                <label className="uc-label">Currency</label>
                <Select
                  value={selectedCurrency}
                  onChange={(option) => {
                    setSelectedCurrency(option);
                    setCompanyData({
                      ...companyData,
                      CompCurrency: option ? option.value : "",
                    });
                  }}
                  options={currencies}
                  styles={dropdownStyles}
                  placeholder="Select Currency"
                  isClearable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuShouldBlockScroll={false}
                  menuShouldScrollIntoView={false}
                  closeMenuOnScroll={false}
                />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-12">
                <label className="uc-label">Time Zone</label>
                <Select
                  value={selectedTimeZone}
                  onChange={(option) => {
                    setSelectedTimeZone(option);
                    setCompanyData((prev) => ({
                      ...prev,
                      CompTimeZone: option ? option.value : "",
                      CompUTCTimeZone: option ? option.label : "",
                    }));
                  }}
                  options={timeZones}
                  styles={dropdownStyles}
                  placeholder="Select Time Zone"
                  isClearable
                  isSearchable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuShouldBlockScroll={false}
                  menuShouldScrollIntoView={false}
                  closeMenuOnScroll={false}
                />
              </div>
            </div>

            <div className="row mb-3">
              <CountryCitySelector
                selectedCountry={selectedCountry}
                selectedCity={selectedCity}
                setSelectedCity={setSelectedCity}
                onChange={handleCountryCityChange}
                setLoading={setLoading}
                selectedValueIsLabel={true}
                isDarkMode={isDarkMode}
              />
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <FormInput
                  label="Address"
                  placeHolder="Enter Company Address"
                  type="text"
                  value={companyData.CompAddress}
                  onchange={(e) => handleInputChange(e, "CompAddress")}
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Code"
                  placeHolder="Enter Company Code"
                  type="text"
                  value={companyData.CompSecCode}
                  onchange={(e) => handleInputChange(e, "CompSecCode")}
                  icon="fa-random"
                  iconTitle="Generate random 6-digit code"
                  iconOnClick={() => {
                    const code = String(Math.floor(100000 + Math.random() * 900000));
                    setCompanyData((prev) => ({ ...prev, CompSecCode: code }));
                  }}
                />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <FormInput
                  label="Push Notification Limit Per Day"
                  placeHolder="Enter Push Notification Limit"
                  type="number"
                  value={companyData.CompPNotificationLimit_PerDay}
                  onchange={(e) => handleInputChange(e, "CompPNotificationLimit_PerDay")}
                  step="10"
                />
              </div>
            </div>

            <div className="row mb-1">
              <div className="col-md-6 mb-3 mb-md-0">
                <label className="uc-label">Remarks</label>
                <textarea
                  className="form-control"
                  placeholder="Enter Company Remarks"
                  value={companyData.CompRemarks}
                  onChange={(e) => handleInputChange(e, "CompRemarks")}
                  rows="3"
                  style={textareaStyle}
                />
              </div>
              <div className="col-md-6">
                <label className="uc-label">Company Detail</label>
                <textarea
                  className="form-control"
                  placeholder="Enter Company Detail"
                  value={companyData.CompDetail}
                  onChange={(e) => handleInputChange(e, "CompDetail")}
                  rows="3"
                  style={textareaStyle}
                />
              </div>
            </div>
          </div>
        </section>

        <section className="uc-section uc-section--nested">
          <div className="uc-section__header">
            <h5>Licenses &amp; Limits</h5>
            <p>Device, user, location, and monthly messaging segment limits for this company</p>
          </div>
          <div className="uc-section__body">
            <div className="row mb-3">
              <div className="col-md-4">
                <FormInput
                  label="Device License"
                  placeHolder="Enter Device License"
                  type="number"
                  value={companyData.CompMaxIPadLicense}
                  onchange={(e) => handleInputChange(e, "CompMaxIPadLicense")}
                  step="1"
                />
              </div>
              <div className="col-md-4">
                <FormInput
                  label="Max User License"
                  placeHolder="Enter Max User License"
                  type="number"
                  value={companyData.CompMaxUserLicense}
                  onchange={(e) => handleInputChange(e, "CompMaxUserLicense")}
                  step="1"
                />
              </div>
              <div className="col-md-4">
                <FormInput
                  label="Admin SMS credits (wallet grant)"
                  placeHolder="One-time admin SMS credits (does not reset monthly)"
                  type="number"
                  value={companyData.CompMonthlySmsLimit}
                  onchange={(e) => handleInputChange(e, "CompMonthlySmsLimit")}
                  step="1"
                />
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-md-4">
                <FormInput
                  label="Monthly WhatsApp Segments"
                  placeHolder="Enter monthly WhatsApp segment limit"
                  type="number"
                  value={companyData.CompMonthlyWhatsAppLimit}
                  onchange={(e) => handleInputChange(e, "CompMonthlyWhatsAppLimit")}
                  step="1"
                />
              </div>
              <div className="col-md-4">
                <FormInput
                  label="Location Licenses"
                  placeHolder="Enter Location Licenses"
                  type="number"
                  value={companyData.LocationLicences}
                  onchange={(e) => handleInputChange(e, "LocationLicences")}
                  step="1"
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-4">
                <FormInput
                  label="Android App Version"
                  placeHolder="Enter Android App Version"
                  type="text"
                  value={companyData.AndroidAppver}
                  onchange={(e) => handleInputChange(e, "AndroidAppver")}
                />
              </div>
              <div className="col-md-4">
                <FormInput
                  label="iOS App Version"
                  placeHolder="Enter iOS App Version"
                  type="text"
                  value={companyData.IOSAppver}
                  onchange={(e) => handleInputChange(e, "IOSAppver")}
                />
              </div>
            </div>
          </div>
        </section>

        <section className="uc-section uc-section--nested">
          <div className="uc-section__header">
            <h5>Dates &amp; Settings</h5>
            <p>Registration window and publish status</p>
          </div>
          <div className="uc-section__body">
            <div className="row mb-3">
              <div className="col-md-6">
                <CustomDateTimePicker
                  label="Registration Start Date"
                  value={companyData.CompRegDate}
                  onChange={(newValue) => handleDateChange(newValue, "CompRegDate")}
                  mode="date"
                  placeholder="Select Date"
                />
              </div>
              <div className="col-md-6">
                <CustomDateTimePicker
                  label="Registration End Date"
                  value={companyData.CompExpireDate}
                  onChange={(newValue) => handleDateChange(newValue, "CompExpireDate")}
                  mode="date"
                  placeholder="Select Date"
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-4">
                <div
                  className="d-flex align-items-center justify-content-between gap-2 p-3 rounded-3"
                  style={{
                    backgroundColor: isDarkMode ? "#1e2427" : "#f8fafc",
                    border: `1px solid ${
                      companyData.CompPublish
                        ? `${themeColor}66`
                        : isDarkMode
                          ? "#3a4044"
                          : "#e2e8f0"
                    }`,
                  }}
                >
                  <div>
                    <div className="fw-semibold" style={{ fontSize: 14 }}>
                      Publish Company
                    </div>
                    <div className="small mt-1" style={{ color: isDarkMode ? "#94a3b8" : "#64748b" }}>
                      {companyData.CompPublish
                        ? "Visible for online ordering and apps."
                        : "Hidden until published."}
                    </div>
                  </div>
                  <ToggleSwitch
                    isChecked={companyData.CompPublish}
                    onToggle={(e) => handleInputChange(e, "CompPublish")}
                    label=""
                    name="CompPublish"
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div
                  className="d-flex align-items-center justify-content-between gap-2 p-3 rounded-3"
                  style={{
                    backgroundColor: isDarkMode ? "#1e2427" : "#f8fafc",
                    border: `1px solid ${
                      companyData.IsOnlineOrderingAllowed
                        ? `${themeColor}66`
                        : isDarkMode
                          ? "#3a4044"
                          : "#e2e8f0"
                    }`,
                  }}
                >
                  <div>
                    <div className="fw-semibold" style={{ fontSize: 14 }}>
                      Online Ordering Allowed
                    </div>
                    <div className="small mt-1" style={{ color: isDarkMode ? "#94a3b8" : "#64748b" }}>
                      {companyData.IsOnlineOrderingAllowed
                        ? "Customers can place online orders."
                        : "Online ordering is disabled."}
                    </div>
                  </div>
                  <ToggleSwitch
                    isChecked={companyData.IsOnlineOrderingAllowed}
                    onToggle={(e) => handleInputChange(e, "IsOnlineOrderingAllowed")}
                    label=""
                    name="IsOnlineOrderingAllowed"
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div
                  className="d-flex align-items-center justify-content-between gap-2 p-3 rounded-3"
                  style={{
                    backgroundColor: isDarkMode ? "#1e2427" : "#f8fafc",
                    border: `1px solid ${
                      companyData.IsReservationAllowed
                        ? `${themeColor}66`
                        : isDarkMode
                          ? "#3a4044"
                          : "#e2e8f0"
                    }`,
                  }}
                >
                  <div>
                    <div className="fw-semibold" style={{ fontSize: 14 }}>
                      Reservation Allowed
                    </div>
                    <div className="small mt-1" style={{ color: isDarkMode ? "#94a3b8" : "#64748b" }}>
                      {companyData.IsReservationAllowed
                        ? "Customers can make reservations."
                        : "Reservations are disabled."}
                    </div>
                  </div>
                  <ToggleSwitch
                    isChecked={companyData.IsReservationAllowed}
                    onToggle={(e) => handleInputChange(e, "IsReservationAllowed")}
                    label=""
                    name="IsReservationAllowed"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  };

  const tabButtonStyle = (isActive) => ({
    border: "none",
    borderBottom: isActive ? `3px solid ${themeColor}` : "3px solid transparent",
    borderRadius: "0.25rem 0.25rem 0 0",
    padding: "0.65rem 0.95rem",
    fontWeight: isActive ? 600 : 500,
    color: isActive ? themeColor : isDarkMode ? "#9ca3af" : "#6b7280",
    backgroundColor: isActive
      ? isDarkMode
        ? "rgba(255,255,255,0.05)"
        : "rgba(0,0,0,0.03)"
      : "transparent",
    whiteSpace: "nowrap",
  });

  return (
    <div className="uc-page">
      {loading && <Preloader />}

      <div className="uc-page__header">
        <div className="uc-page__title-block">
          <ol className="breadcrumb mb-1">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/company/manageCompany">Manage Company</Link>
            </li>
            <li className="breadcrumb-item active">Update Company</li>
          </ol>
          <h1 className="page-header mb-0">Update Company</h1>
          {companyData.CompName ? (
            <p className="uc-page__subtitle">{companyData.CompName}</p>
          ) : null}
        </div>

        <div className="uc-page__actions">
          {activeTab === "general" && (
            <button
              type="button"
              onClick={handleSubmit}
              className="uc-btn-primary"
              disabled={loading}
              style={{
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                boxShadow: `0 4px 14px ${themeColor}40`,
              }}
            >
              <i className="fa fa-save" aria-hidden />
              {loading ? "Updating..." : "Update"}
            </button>
          )}
          {activeTab === "staff" && (
            <button
              type="button"
              className="uc-btn-primary"
              style={{
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                boxShadow: `0 4px 14px ${themeColor}40`,
              }}
              onClick={() => {
                setShowAddStaffModal(true);
                window.dispatchEvent(new CustomEvent("openCompanyStaffAddModal"));
              }}
            >
              <i className="fa fa-user-plus" aria-hidden />
              Add staff member
            </button>
          )}
        </div>
      </div>

      <div className="uc-shell">
        <ul
          className="nav nav-tabs uc-shell__tabs flex-nowrap mb-0"
          role="tablist"
          style={{
            borderBottom: `2px solid ${isDarkMode ? "#374151" : "#dee2e6"}`,
          }}
        >
          {COMPANY_TABS.map((tab) => (
            <li className="nav-item" role="presentation" key={tab.id}>
              <button
                type="button"
                role="tab"
                className={`nav-link ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
                style={tabButtonStyle(activeTab === tab.id)}
              >
                <i className={`fa ${tab.icon}`} aria-hidden />
                {tab.label}
              </button>
            </li>
          ))}
        </ul>
        <div className="uc-shell__body">{renderTabContent()}</div>
      </div>
    </div>
  );
};

export default UpdateCompany;